import React from 'react';

import {
  Alert,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
} from '@mui/material';

import { getCancellationPolicyLabel } from '~/utils/cancellationPolicy';

import { RATE_PLAN_FORM_ID, getRatePlanEnumOptions } from './utils';
import type { RatePlanCreateSchema } from './utils';

interface Props {
  createdRatePlan: App.RatePlan | null;
  hasSchemaError: boolean;
  isLoadingSchema: boolean;
  onCancellationPolicyChange: (value: string) => void;
  onClearError: () => void;
  onProductTypeChange: (value: string) => void;
  onRatePlanNameChange: (value: string) => void;
  onRateTypeChange: (value: string) => void;
  onRetrySchema: () => void;
  onStatusChange: (value: string) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  ratePlanCancellationPolicy: string;
  ratePlanCreateSchema: RatePlanCreateSchema | undefined;
  ratePlanName: string;
  ratePlanProductType: string;
  ratePlanRateType: string;
  ratePlanStatus: string;
}

export default function RatePlanStep({
  createdRatePlan,
  hasSchemaError,
  isLoadingSchema,
  onCancellationPolicyChange,
  onClearError,
  onProductTypeChange,
  onRatePlanNameChange,
  onRateTypeChange,
  onRetrySchema,
  onStatusChange,
  onSubmit,
  ratePlanCancellationPolicy,
  ratePlanCreateSchema,
  ratePlanName,
  ratePlanProductType,
  ratePlanRateType,
  ratePlanStatus,
}: Props) {
  const cancellationPolicyOptions = getRatePlanEnumOptions(
    ratePlanCreateSchema,
    'cancellation_policy',
    ratePlanCancellationPolicy,
  );
  const statusOptions = getRatePlanEnumOptions(ratePlanCreateSchema, 'status', ratePlanStatus);
  const productTypeOptions = getRatePlanEnumOptions(ratePlanCreateSchema, 'product_type', ratePlanProductType);
  const rateTypeOptions = getRatePlanEnumOptions(ratePlanCreateSchema, 'rate_type', ratePlanRateType);

  return (
    <form id={RATE_PLAN_FORM_ID} onSubmit={onSubmit}>
      {createdRatePlan && (
        <Alert severity="success">Flash rate plan {createdRatePlan.name || createdRatePlan.id} has been created.</Alert>
      )}
      {!createdRatePlan && isLoadingSchema && (
        <Alert icon={<CircularProgress size={16} />} severity="info">
          Loading rate plan details.
        </Alert>
      )}
      {!createdRatePlan && !isLoadingSchema && hasSchemaError && (
        <Alert
          severity="error"
          action={
            <Button
              onClick={() => {
                onClearError();
                onRetrySchema();
              }}
            >
              Try again
            </Button>
          }
        >
          Unable to load rate plan details.
        </Alert>
      )}
      {!createdRatePlan && !isLoadingSchema && !hasSchemaError && (
        <Stack spacing={2}>
          <TextField
            label="Rate Plan Name"
            value={ratePlanName}
            onChange={(event) => {
              onRatePlanNameChange(event.target.value);
              onClearError();
            }}
            required
            fullWidth
          />
          <FormControl fullWidth required>
            <InputLabel id="flash-offer-rate-plan-cancellation-policy-label">Cancellation Policy</InputLabel>
            <Select
              label="Cancellation Policy"
              labelId="flash-offer-rate-plan-cancellation-policy-label"
              value={ratePlanCancellationPolicy}
              onChange={(event) => {
                onCancellationPolicyChange(event.target.value);
                onClearError();
              }}
            >
              {cancellationPolicyOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {getCancellationPolicyLabel(option)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth required>
            <InputLabel id="flash-offer-rate-plan-status-label">Status</InputLabel>
            <Select
              label="Status"
              labelId="flash-offer-rate-plan-status-label"
              value={ratePlanStatus}
              onChange={(event) => {
                onStatusChange(event.target.value);
                onClearError();
              }}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth required>
            <InputLabel id="flash-offer-rate-plan-product-type-label">Product Type</InputLabel>
            <Select
              label="Product Type"
              labelId="flash-offer-rate-plan-product-type-label"
              value={ratePlanProductType}
              onChange={(event) => {
                onProductTypeChange(event.target.value);
                onClearError();
              }}
            >
              {productTypeOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl required>
            <FormLabel>Rate Type</FormLabel>
            <RadioGroup
              row
              value={ratePlanRateType}
              onChange={(event) => {
                onRateTypeChange(event.target.value);
                onClearError();
              }}
            >
              {rateTypeOptions.map((option) => (
                <FormControlLabel key={option} value={option} control={<Radio />} label={option} />
              ))}
            </RadioGroup>
          </FormControl>
        </Stack>
      )}
    </form>
  );
}
